import { useTimeoutFn } from '@vueuse/core';

const HOLD_DELAY = 200;
const DOUBLE_TAP_DELAY = 300;

interface UseVideoTouchGesturesOptions {
	videoElementRef: Ref<HTMLVideoElement | null>;
	isAutoplayBlocked: Ref<boolean>;
	onSingleTap: () => void;
	onDoubleTap: () => void;
}

export function useVideoTouchGestures(options: UseVideoTouchGesturesOptions) {
	const { videoElementRef, isAutoplayBlocked, onSingleTap, onDoubleTap } =
		options;

	const isHolding = ref(false);
	const wasPlayingBeforeHold = ref(false);
	const suppressNextTap = ref(false);

	const { start: startHoldDelay, stop: stopHoldDelay } = useTimeoutFn(
		() => {
			if (videoElementRef.value && !videoElementRef.value.paused) {
				isHolding.value = true;
				wasPlayingBeforeHold.value = true;
				videoElementRef.value.pause();
			}
		},
		HOLD_DELAY,
		{ immediate: false }
	);

	let lastTapTime = 0;
	const { start: startSingleTapDelay, stop: stopSingleTapDelay } = useTimeoutFn(
		() => {
			if (lastTapTime !== 0 && !isHolding.value) {
				onSingleTap();
				lastTapTime = 0;
			}
		},
		DOUBLE_TAP_DELAY,
		{ immediate: false }
	);

	function handleTouchStart() {
		if (!import.meta.client) return;
		startHoldDelay();
	}

	function handleTouchEnd() {
		if (!import.meta.client) return;
		stopHoldDelay();

		if (isHolding.value) {
			suppressNextTap.value = true;
		}

		if (
			isHolding.value &&
			wasPlayingBeforeHold.value &&
			videoElementRef.value
		) {
			videoElementRef.value.play().catch(() => {});
		}
		isHolding.value = false;
		wasPlayingBeforeHold.value = false;
	}

	function handleTap() {
		if (!import.meta.client) return;

		if (suppressNextTap.value) {
			suppressNextTap.value = false;
			return;
		}

		if (isHolding.value) return;

		// When autoplay is blocked, emit single-tap immediately so play() runs
		// synchronously inside the click handler — iOS requires this for the
		// user-activation to be valid. The 300ms double-tap delay would expire it.
		if (isAutoplayBlocked.value) {
			onSingleTap();
			return;
		}

		const now = Date.now();
		const timeSinceLastTap = now - lastTapTime;

		if (timeSinceLastTap < DOUBLE_TAP_DELAY) {
			stopSingleTapDelay();
			lastTapTime = 0;
			onDoubleTap();
		} else {
			lastTapTime = now;
			startSingleTapDelay();
		}
	}

	/** Cancel any pending single-tap handler and forget the last tap timestamp. */
	function cancelPendingTap() {
		stopSingleTapDelay();
		lastTapTime = 0;
	}

	function cleanup() {
		stopSingleTapDelay();
		stopHoldDelay();
	}

	return {
		isHolding,
		handleTouchStart,
		handleTouchEnd,
		handleTap,
		cancelPendingTap,
		cleanup
	};
}
